/**
 * Focus-mode helpers shared by the Map view.
 *
 * Focus = "show only the connected subgraph of one node". Useful to
 * cut through visual clutter on a dense topology graph: click on a
 * canonical and see only its producers + consumers, dimming everything
 * else.
 */
import type { TopologyEdge } from "@/lib/api-topology";


/**
 * BFS both upstream and downstream from ``nodeId``, collecting every
 * reachable node id. The starting node is always included. Returns
 * ``null`` when no focus is active — callers should treat ``null`` as
 * "no dimming".
 */
export function reachableFrom(
  nodeId: string | null,
  edges: TopologyEdge[],
): Set<string> | null {
  if (!nodeId) return null;
  const visited = new Set<string>([nodeId]);
  const stack = [nodeId];
  while (stack.length > 0) {
    const cur = stack.pop()!;
    for (const e of edges) {
      if (e.source === cur && !visited.has(e.target)) {
        visited.add(e.target);
        stack.push(e.target);
      }
      if (e.target === cur && !visited.has(e.source)) {
        visited.add(e.source);
        stack.push(e.source);
      }
    }
  }
  return visited;
}


/** Returns true if the edge should be kept fully visible in focus mode. */
export function edgeInFocus(
  edge: TopologyEdge,
  focus: Set<string> | null,
): boolean {
  if (focus === null) return true;
  return focus.has(edge.source) && focus.has(edge.target);
}
